import { useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Cloud,
  CloudDownload,
  CloudOff,
  CloudUpload,
  FileEdit,
  FileText,
  Loader2,
  PenSquare,
  Search,
  Trash2,
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useArticleDrafts } from '@/hooks/useArticleDrafts';
import { usePublicationDrafts } from '@/hooks/usePublicationDrafts';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useDraftSync } from '@/hooks/useDraftSync';
import { useToast } from '@/hooks/useToast';
import { draftTitle, type ArticleDraft } from '@/lib/articleDrafts';
import { publicationDraftTitle, type PublicationDraft } from '@/lib/publicationDrafts';
import { cn } from '@/lib/utils';

/** Builds the editor URL that resumes an article draft. */
function draftEditPath(draft: ArticleDraft): string {
  const slug = draft.slug || 'untitled';
  return `/w/${encodeURIComponent(slug)}/edit?draft=${encodeURIComponent(draft.id)}`;
}

/**
 * Builds the publication editor URL that resumes a draft.
 *
 * A draft that has never been published resumes at `/p/new`, not
 * `/p/<title>/edit` — the `:d` route makes the editor treat the slug as
 * an existing publication and try to fetch it from relays, which for a
 * never-published draft means a pointless query and a loading spinner
 * for a publication that doesn't exist anywhere.
 */
function publicationDraftEditPath(draft: PublicationDraft): string {
  const query = `?draft=${encodeURIComponent(draft.id)}`;
  return draft.d ? `/p/${encodeURIComponent(draft.d)}/edit${query}` : `/p/new${query}`;
}

/** One interleaved list entry — either kind of draft, unified for display. */
type DraftRow =
  | { kind: 'article'; draft: ArticleDraft }
  | { kind: 'publication'; draft: PublicationDraft };

function rowTitle(row: DraftRow): string {
  return row.kind === 'article' ? draftTitle(row.draft) : publicationDraftTitle(row.draft);
}

function rowSummary(row: DraftRow): string {
  if (row.draft.summary.trim()) return row.draft.summary.trim();
  if (row.kind === 'article') return row.draft.content.trim().slice(0, 120);
  return '';
}

/** Compact relative time: "just now", "5m ago", "2h ago", "3d ago", then a locale date. */
function relativeTime(timestamp: number): string {
  const seconds = Math.max(0, Math.floor(Date.now() / 1000 - timestamp / 1000));
  if (seconds < 90) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Inline sync-state indicator on a draft row. Article drafts show "local
 * only" until first synced, then the relay hostnames (or a count) per the
 * last successful sync; publication drafts state "this device only"
 * plainly, since they deliberately never leave the browser.
 */
function RowSyncBadge({ row }: { row: DraftRow }) {
  const { getSyncRecord } = useDraftSync();

  if (row.kind === 'publication') {
    return (
      <span className="text-muted-foreground/60" title="Publication drafts stay on this device">
        · this device only
      </span>
    );
  }

  const record = getSyncRecord(row.draft.id);
  if (!record) {
    return <span className="text-muted-foreground/60">· local only</span>;
  }

  const names = relayHostnames(record.relays);
  const label =
    names.length > 0 && names.length === record.relays.length
      ? `· synced (${names.length === 1 ? 'relay' : 'relays'}: ${names.join(', ')})`
      : `· synced (${record.relays.length} ${record.relays.length === 1 ? 'relay' : 'relays'})`;

  return (
    <span
      className="text-emerald-600 dark:text-emerald-500"
      title={names.length > 0 ? 'Relays: ' + names.join(', ') : 'Synced'}
    >
      {label}
    </span>
  );
}

function relayHostnames(relays: string[] | undefined): string[] {
  if (!relays || relays.length === 0) return [];
  return [...new Set(
    relays
      .map((url) => {
        try {
          return new URL(url).host;
        } catch {
          return url;
        }
      })
      .filter(Boolean),
  )];
}

/**
 * `/drafts` — every unfinished article and publication the current user
 * has saved locally on this device, most recently worked on first.
 *
 * Drafts are device-local and scoped per Nostr identity (see
 * `useArticleDrafts` / `usePublicationDrafts`), so a logged-out visitor
 * has no drafts to list — but they can still start writing; the editor
 * saves drafts once they're logged in.
 *
 * Both kinds render as one interleaved, filterable list (the Media
 * page's idiom) rather than stacked sections — a type icon and the sync
 * badge carry the difference, since they resume into different editors
 * and have different sync stories: article drafts sync to relays
 * NIP-44-encrypted, publication drafts stay on this device.
 */
export function DraftsPage() {
  useSeoMeta({ title: 'Drafts — Wikistar' });

  const { user } = useCurrentUser();
  const { drafts, count, remove, clear } = useArticleDrafts();
  const { drafts: publicationDrafts, count: publicationDraftCount, remove: removePublicationDraft, clear: clearPublicationDrafts } = usePublicationDrafts();
  const { isSyncing, syncDown, syncAll, deleteSynced, canSync, getSyncRecord } = useDraftSync();
  const { toast } = useToast();
  const [confirmClear, setConfirmClear] = useState(false);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'all' | 'article' | 'publication'>('all');
  const [syncFilter, setSyncFilter] = useState<'all' | 'synced' | 'local'>('all');

  // One interleaved list, most recently worked on first.
  const rows = useMemo<DraftRow[]>(
    () =>
      [
        ...drafts.map((draft): DraftRow => ({ kind: 'article', draft })),
        ...publicationDrafts.map((draft): DraftRow => ({ kind: 'publication', draft })),
      ].sort((a, b) => b.draft.updatedAt - a.draft.updatedAt),
    [drafts, publicationDrafts],
  );

  // Sync tallies for the summary strip. `isSyncing` is a dep so the
  // numbers (and the sync filter below) re-read the records right after
  // a sync completes — the records live in localStorage, outside React's
  // reactivity.
  const syncedCount = useMemo(
    () => drafts.filter((draft) => Boolean(getSyncRecord(draft.id))).length,
    [drafts, isSyncing, getSyncRecord],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (kind !== 'all' && row.kind !== kind) return false;
      if (syncFilter !== 'all') {
        const synced = row.kind === 'article' && Boolean(getSyncRecord(row.draft.id));
        if (syncFilter === 'synced' && !synced) return false;
        if (syncFilter === 'local' && synced) return false;
      }
      if (needle) {
        const slug = row.kind === 'article' ? row.draft.slug : (row.draft.d ?? '');
        const haystack = `${rowTitle(row)} ${rowSummary(row)} ${slug}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, kind, syncFilter, query, isSyncing, getSyncRecord]);

  const totalCount = count + publicationDraftCount;
  const localCount = totalCount - syncedCount;

  const handleSyncDown = async () => {
    try {
      const pulled = await syncDown();
      toast({
        title: pulled === undefined || pulled === 0 ? 'Nothing to pull' : `Pulled ${pulled} draft${pulled === 1 ? '' : 's'}`,
        description:
          pulled === undefined || pulled === 0
            ? 'Your local library is already up to date, or your signer/relays returned nothing.'
            : 'Your drafts are up to date across your devices.',
      });
    } catch (error) {
      toast({
        title: "Couldn't sync drafts",
        description: error instanceof Error ? error.message : 'Something went wrong.',
        variant: 'destructive',
      });
    }
  };

  const handleSyncAll = async () => {
    try {
      await syncAll();
      toast({
        title: 'Drafts backed up',
        description: 'All your drafts are now encrypted and synced to Nostr.',
      });
    } catch (error) {
      toast({
        title: "Couldn't sync drafts",
        description: error instanceof Error ? error.message : 'Something went wrong.',
        variant: 'destructive',
      });
    }
  };

  const handleRemove = (row: DraftRow) => {
    if (row.kind === 'article') {
      remove(row.draft.id);
      // Best-effort cleanup of the synced copy on relays.
      void deleteSynced(row.draft.id).catch(() => undefined);
    } else {
      removePublicationDraft(row.draft.id);
    }
  };

  const handleClearAll = () => {
    clear();
    clearPublicationDrafts();
    // Best-effort cleanup of synced copies for every local article draft.
    // Publication drafts have no Nostr-synced copy, so there's nothing to
    // clean up there.
    for (const draft of drafts) {
      void deleteSynced(draft.id).catch(() => undefined);
    }
    setConfirmClear(false);
  };

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <FileEdit className="size-6" />
              Drafts
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Unfinished work saved in this browser. Article drafts sync across your devices,
              encrypted to you; publication drafts stay on this device.
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {/* Sync covers article drafts only — the titles say so,
                because a bare "Sync up" next to a list containing
                publication drafts would imply those travel too. */}
            {user && canSync && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSyncDown}
                  disabled={isSyncing}
                  title="Pull encrypted article drafts from your relays"
                >
                  {isSyncing ? <Loader2 className="size-4 animate-spin" /> : <CloudDownload className="size-4" />}
                  Sync down
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSyncAll}
                  disabled={isSyncing}
                  title="Push your article drafts to your relays, encrypted to yourself"
                >
                  {isSyncing ? <Loader2 className="size-4 animate-spin" /> : <CloudUpload className="size-4" />}
                  Sync up
                </Button>
              </>
            )}
            {totalCount > 0 && (
              <Button type="button" variant="outline" size="sm" onClick={() => setConfirmClear(true)}>
                Delete all
              </Button>
            )}
          </div>
        </div>

        {!user ? (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                Log in with a Nostr key, extension, or bunker to save and resume drafts.
              </p>
            </CardContent>
          </Card>
        ) : totalCount === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-md text-muted-foreground">
                No drafts yet. While writing an article or publication, use{' '}
                <span className="font-medium text-foreground">Save draft</span> to keep an unfinished copy in this
                browser — you can pick it up here later.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Button asChild>
                  <Link to="/w/new-article/edit">
                    <PenSquare className="size-4" />
                    New article
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/p/new">
                    <BookOpen className="size-4" />
                    New publication
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Drafts summary — same idiom as the media page's storage
                strip: honest counts at a glance, no headings. */}
            <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <FileEdit className="size-3.5" />
                {totalCount} draft{totalCount === 1 ? '' : 's'}
              </span>
              {syncedCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <Cloud className="size-3.5" />
                  {syncedCount} synced
                </span>
              )}
              {localCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <CloudOff className="size-3.5" />
                  {localCount} local only
                </span>
              )}
            </div>

            <div className="mb-4 space-y-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search drafts by title, summary, or slug…"
                  className="pl-9"
                />
              </div>

              <div className="flex flex-wrap gap-1.5">
                <FilterChip active={kind === 'all'} onClick={() => setKind('all')}>
                  All
                </FilterChip>
                <FilterChip active={kind === 'article'} onClick={() => setKind('article')}>
                  <FileText className="size-3" />
                  Articles
                </FilterChip>
                <FilterChip active={kind === 'publication'} onClick={() => setKind('publication')}>
                  <BookOpen className="size-3" />
                  Publications
                </FilterChip>
                <span className="mx-1 w-px self-stretch bg-border" aria-hidden />
                <FilterChip active={syncFilter === 'synced'} onClick={() => setSyncFilter(syncFilter === 'synced' ? 'all' : 'synced')}>
                  <Cloud className="size-3" />
                  Synced
                </FilterChip>
                <FilterChip active={syncFilter === 'local'} onClick={() => setSyncFilter(syncFilter === 'local' ? 'all' : 'local')}>
                  <CloudOff className="size-3" />
                  Local only
                </FilterChip>
              </div>
            </div>

            {filtered.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 px-8 text-center">
                  <p className="mx-auto max-w-sm text-muted-foreground">Nothing matches those filters.</p>
                </CardContent>
              </Card>
            ) : (
              <ul className="space-y-2">
                {filtered.map((row) => (
                  <DraftRowCard key={row.draft.id} row={row} onRemove={() => handleRemove(row)} />
                ))}
              </ul>
            )}
          </>
        )}

        <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all drafts?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes all {totalCount} draft{totalCount === 1 ? '' : 's'} — articles and
                publications — saved in this browser, and can't be undone.
                {canSync && (
                  <>
                    {' '}
                    Any article draft you synced was stored on your relays encrypted to yourself; a deletion
                    request is sent, but gift-wrapped copies often can't be removed once published, so assume
                    the encrypted copy may persist.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleClearAll}>
                Delete all
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

/** One draft row: type icon, title/summary link, meta line, delete. */
function DraftRowCard({ row, onRemove }: { row: DraftRow; onRemove: () => void }) {
  const title = rowTitle(row);
  const summary = rowSummary(row);
  const editPath = row.kind === 'article' ? draftEditPath(row.draft) : publicationDraftEditPath(row.draft);

  return (
    <li>
      <Card>
        <CardContent className="flex items-center gap-3 py-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted" aria-hidden>
            {row.kind === 'article' ? (
              <FileText className="size-4 text-muted-foreground" />
            ) : (
              <BookOpen className="size-4 text-muted-foreground" />
            )}
          </span>
          <Link to={editPath} className="min-w-0 flex-1 group">
            <p className="font-medium break-words group-hover:underline">{title}</p>
            {summary && (
              <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground break-words">{summary}</p>
            )}
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground/70">
              {row.kind === 'article' && row.draft.slug && (
                <>
                  <span className="font-mono">{row.draft.slug}</span>
                  <span aria-hidden>·</span>
                </>
              )}
              {row.kind === 'publication' && (
                <>
                  <span>
                    {row.draft.sections.length} section{row.draft.sections.length === 1 ? '' : 's'}
                  </span>
                  <span aria-hidden>·</span>
                </>
              )}
              {row.draft.updatedAt > 0 && (
                <time dateTime={new Date(row.draft.updatedAt).toISOString()} title={new Date(row.draft.updatedAt).toLocaleString()}>
                  {relativeTime(row.draft.updatedAt)}
                </time>
              )}
              <RowSyncBadge row={row} />
            </p>
          </Link>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
            title="Delete draft"
            aria-label={`Delete draft "${title}"`}
          >
            <Trash2 className="size-4" />
          </Button>
        </CardContent>
      </Card>
    </li>
  );
}
